<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { useRouter } from "vue-router";

import {
  askProject,
  searchProject,
  type AskResponse,
  type SearchHit,
} from "@/api/client";

const props = defineProps<{ projectId: string; open: boolean }>();
const emit = defineEmits<{ (e: "close"): void }>();

const router = useRouter();
const input = ref<HTMLInputElement | null>(null);
const q = ref("");
const results = ref<SearchHit[]>([]);
const enabled = ref(true);
const searching = ref(false);
const answer = ref<AskResponse | null>(null);
const asking = ref(false);

const TYPE_LABEL: Record<string, string> = {
  defect: "Defect",
  observation: "Observation",
  check_sheet: "Check sheet",
  site_diary: "Site diary",
  briefing: "Briefing",
  work_pack: "Work pack",
  library_doc: "Document",
};

// Map a hit to its in-app route (library docs have no detail page yet).
const TYPE_PATH: Record<string, string> = {
  defect: "defects",
  observation: "observations",
  check_sheet: "check-sheets",
  site_diary: "site-diaries",
  briefing: "briefings",
  work_pack: "work-packs",
};

function hitTo(hit: SearchHit): string | null {
  const seg = TYPE_PATH[hit.source_type];
  return seg ? `/projects/${props.projectId}/${seg}/${hit.source_id}` : null;
}

let debounce: ReturnType<typeof setTimeout> | undefined;
watch(q, (value) => {
  answer.value = null;
  clearTimeout(debounce);
  const term = value.trim();
  if (term.length < 2) {
    results.value = [];
    return;
  }
  debounce = setTimeout(() => void runSearch(term), 220);
});

async function runSearch(term: string): Promise<void> {
  searching.value = true;
  try {
    const res = await searchProject(props.projectId, term);
    enabled.value = res.enabled;
    results.value = res.results;
  } catch {
    results.value = [];
  } finally {
    searching.value = false;
  }
}

async function ask(): Promise<void> {
  const term = q.value.trim();
  if (term.length < 2) return;
  asking.value = true;
  answer.value = null;
  try {
    answer.value = await askProject(props.projectId, term);
  } finally {
    asking.value = false;
  }
}

function go(hit: SearchHit): void {
  const to = hitTo(hit);
  if (to) {
    emit("close");
    void router.push(to);
  }
}

const showAskPrompt = computed(() => q.value.trim().length >= 2);

watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      await nextTick();
      input.value?.focus();
    } else {
      q.value = "";
      results.value = [];
      answer.value = null;
    }
  },
);
</script>

<template>
  <div
    v-if="open"
    class="fixed inset-0 z-50 flex items-start justify-center bg-neutral-900/40 px-4 pt-[10vh]"
    @click.self="emit('close')"
    @keydown.esc="emit('close')"
  >
    <div class="w-full max-w-2xl overflow-hidden rounded-xl bg-neutral-0 shadow-xl">
      <!-- Input -->
      <div class="flex items-center gap-2 border-b border-neutral-100 px-4">
        <svg
          class="h-5 w-5 shrink-0 text-neutral-400"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle
            cx="11"
            cy="11"
            r="7"
          />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          ref="input"
          v-model="q"
          type="text"
          placeholder="Search by meaning — e.g. 'crack near a window'"
          class="flex-1 bg-transparent py-3.5 text-sm outline-none placeholder:text-neutral-400"
          @keydown.enter="ask"
          @keydown.esc="emit('close')"
        >
        <button
          type="button"
          class="rounded border border-neutral-200 px-1.5 py-0.5 text-xs text-neutral-400 hover:bg-neutral-50"
          @click="emit('close')"
        >
          Esc
        </button>
      </div>

      <div class="max-h-[60vh] overflow-y-auto">
        <!-- Helper / empty state -->
        <p
          v-if="q.trim().length < 2"
          class="px-4 py-6 text-sm text-neutral-500"
        >
          Type at least 2 characters. Searches by meaning — “broken floor” finds
          “cracked tile in lobby”. Or ask a full question to get an AI answer.
        </p>

        <p
          v-else-if="!enabled"
          class="px-4 py-6 text-sm text-neutral-500"
        >
          Semantic search isn't switched on yet for this environment.
        </p>

        <template v-else>
          <!-- Ask the project -->
          <button
            v-if="showAskPrompt"
            type="button"
            class="flex w-full items-center gap-2 border-b border-neutral-100 px-4 py-3 text-left text-sm hover:bg-neutral-50"
            :disabled="asking"
            @click="ask"
          >
            <span class="rounded bg-brand-primary/10 px-2 py-0.5 text-xs font-medium text-brand-primary">Ask</span>
            <span class="text-neutral-700">{{ asking ? "Asking the project…" : `Ask the project: “${q.trim()}”` }}</span>
          </button>

          <!-- AI answer -->
          <div
            v-if="answer"
            class="border-b border-neutral-100 bg-neutral-50 px-4 py-3"
          >
            <p
              v-if="answer.answer"
              class="whitespace-pre-wrap text-sm text-neutral-800"
            >
              {{ answer.answer }}
            </p>
            <p
              v-else
              class="text-sm text-neutral-500"
            >
              No AI answer available (AI isn't configured) — see the matching
              records below.
            </p>
            <ul
              v-if="answer.citations.length"
              class="mt-2 grid gap-0.5"
            >
              <li
                v-for="c in answer.citations"
                :key="c.n"
                class="text-xs text-neutral-500"
              >
                <button
                  type="button"
                  class="text-left hover:text-brand-primary hover:underline"
                  @click="go({ source_type: c.source_type, source_id: c.source_id, title: c.title, snippet: '', score: 0 })"
                >
                  [{{ c.n }}] {{ TYPE_LABEL[c.source_type] || c.source_type }} — {{ c.title }}
                </button>
              </li>
            </ul>
          </div>

          <!-- Results -->
          <p
            v-if="!searching && !results.length"
            class="px-4 py-6 text-sm text-neutral-500"
          >
            No matches.
          </p>
          <ul>
            <li
              v-for="hit in results"
              :key="hit.source_id"
            >
              <button
                type="button"
                class="flex w-full items-start gap-3 border-b border-neutral-50 px-4 py-3 text-left hover:bg-neutral-50"
                :class="hitTo(hit) ? '' : 'cursor-default'"
                @click="go(hit)"
              >
                <span class="mt-0.5 shrink-0 rounded bg-neutral-100 px-1.5 py-0.5 text-xs text-neutral-500">
                  {{ TYPE_LABEL[hit.source_type] || hit.source_type }}
                </span>
                <span class="min-w-0">
                  <span class="block truncate text-sm font-medium text-neutral-900">{{ hit.title }}</span>
                  <span class="block truncate text-xs text-neutral-500">{{ hit.snippet }}</span>
                </span>
              </button>
            </li>
          </ul>
        </template>
      </div>
    </div>
  </div>
</template>
